import { Store } from 'vuex';
import groupBy from 'lodash/groupBy';

import { AppState } from '@/store';
import { MUTATIONS } from '@/store/meal-builder';
import { Logger } from '@/services/logger/logger';

import { portionItemTypeToItemCardPortion, findItemsReferencing, getExtraPortions, findItemReferencing, getHalfPortions } from './utils';

import {
  MenuItemType,
  PortionItemType,
  BuilderMode,
  SelectableVariantMenuItem,
  SelectableMenuItem,
  ItemCardCustomization,
  SelectableItemPortion,
  MealBuilderItemSection,
  OrderMealRequest,
  SelectableMenuItemSource,
  MenuItemCategory,
  BasicOrderMealItem,
  ItemCardCustomizationType,
  MenuItemCardCustomization,
  ItemCardSelectionInfoKind,
  MenuMetadataForChannel,
  PricingInformation,
  EntreeBuilderMode,
} from '@/models';

import { arrayToMap, partition, any, joinWithSuffix, isDefined } from '@/helpers';
import { MealBuilderItemSelector } from './item-selectors';
import { UtilsService } from '@/services/utils/utils.service';
import { MenuService } from '@/services/menu/menu.service';
import { MealBuilderAnalyticService } from './meal-builder-analytics.service';
import { AnalyticsService } from '../analytics/analytics.service';

const HIDDEN_ITEM_TYPES = new Set<MenuItemType>([PortionItemType.HalfPortion, PortionItemType.ExtraPortion]);

export interface MealBuilderVariantService<TBuilderMode extends BuilderMode> {
  selectVariantSafe(
    builderMode: TBuilderMode,
    chosenVariant: SelectableVariantMenuItem,
    currentVariant: SelectableVariantMenuItem | null,
    allVariants: SelectableVariantMenuItem[],
    args: any,
    shouldTrackAnalytics?: boolean,
  ): void;

  selectDefaultContents(builderMode: TBuilderMode, variant: SelectableVariantMenuItem): void;

  toggleVariantCustomizationSafe(
    builderMode: TBuilderMode,
    variant: SelectableMenuItem,
    allVariants: SelectableVariantMenuItem[],
    customization: ItemCardCustomization,
    shouldTrackAnalytics?: boolean,
  ): void;

  getItemSections(builderMode: TBuilderMode, variant: SelectableVariantMenuItem | null): MealBuilderItemSection[];
  isItemDisplayedAsSelected(builderMode: TBuilderMode, item: SelectableMenuItem): boolean;
  getDisplayedItemPortion(builderMode: TBuilderMode, variant: SelectableMenuItem, currentVariant: SelectableVariantMenuItem | null): SelectableItemPortion | undefined;
  getEffectiveItemPrice(builderMode: BuilderMode, item: SelectableMenuItem, variant: SelectableVariantMenuItem | null): PricingInformation;

  getVariants(builderMode: TBuilderMode): SelectableVariantMenuItem[];
  getVariantsItemSectionTitle(builderMode: TBuilderMode): string;
  getVariantsItemSectionSubtitle(builderMode: TBuilderMode, variant: SelectableVariantMenuItem): string | undefined;
  getDefaultVariant(builderMode: TBuilderMode): SelectableVariantMenuItem | undefined;

  validateMealBuilder(mode: TBuilderMode, metadata: MenuMetadataForChannel, itemSections: MealBuilderItemSection[]): { error: string | null };
  buildMealRequest(mode: TBuilderMode, items: SelectableMenuItem[], mealName: string): OrderMealRequest | undefined;
  setBuilderMode(builderMode: BuilderMode): void;
}

export abstract class AbstractMealBuilderItemVariantService<TBuilderMode extends BuilderMode> implements MealBuilderVariantService<TBuilderMode> {
  constructor(
    protected utils: UtilsService,
    protected menu: MenuService,
    protected store: Store<AppState>,
    protected itemSelector: MealBuilderItemSelector,
    protected logger: Logger,
    protected mealBuilderAnalytics: MealBuilderAnalyticService,
    protected analytics: AnalyticsService,
  ) {}

  getItemSections(builderMode: TBuilderMode, variant: SelectableVariantMenuItem | null) {
    const metadata = this.store.state.menu.metadata!;
    const resolvedVariant = variant ?? this.getDefaultVariant(builderMode);
    const itemSectionContentGroupsRules = arrayToMap(resolvedVariant?.item.contentGroups ?? [], g => g.contentGroupName); //from menu to enforce group/section rules

    const itemType = resolvedVariant!.item!.itemType;

    const metadataGroup = metadata.groups.find(group => group.menuItemType == itemType);
    if (!metadataGroup) {
      // common in our tests
      return [];
    }

    const metadataItemSections = metadataGroup!.itemSections.map(sectionKey => {
      const metadataSection = metadata.itemSections[sectionKey];
      metadataSection!.name = sectionKey;
      return metadataSection;
    });

    const itemsMap: { [key: string]: SelectableMenuItem } = {};
    const hiddenItems: SelectableMenuItem[] = [];

    resolvedVariant!.contents.forEach(i => {
      if (this.isItemTypeHidden(i.item.itemType)) {
        hiddenItems.push(i);
      } else {
        itemsMap[i.item.itemId] = i;
      }
    });

    (builderMode as EntreeBuilderMode).sides.forEach(i => (itemsMap[i.item.itemId] = i));
    (builderMode as EntreeBuilderMode).drinks.forEach(i => (itemsMap[i.item.itemId] = i));

    //Hidden items feel really awkward as a section. Perhaps they could be their own thing if we rewrite?
    const hiddenItemSections = Object.entries(groupBy(hiddenItems, i => i.item.itemType)).map(value => {
      const [itemType, sectionItems] = value;

      const section: MealBuilderItemSection = {
        id: itemType,
        title: itemType,
        hidden: this.isItemTypeHidden(itemType),
        showSelectNone: null,
        minQuantity: null,
        maxQuantity: null,
        maxCustomizations: null,
        items: [...sectionItems],
      };

      return section;
    });

    const itemSections = metadataItemSections
      .map(section => {
        const contentGroup = itemSectionContentGroupsRules[section!.name];

        const itemSection: MealBuilderItemSection = {
          id: section!.name, //match the current outputs to whats in metadata and figureout the mapping
          title: section!.alias,
          hidden: this.isItemTypeHidden(itemType),
          showSelectNone: metadata.itemSections[section!.name]?.hasSelectNoneOption || null,
          minQuantity: contentGroup?.minQuantity ?? null,
          maxQuantity: contentGroup?.maxQuantity ?? null,
          maxCustomizations: null,
          items: metadata.itemSections[section!.name]!.items.map(mdItem => itemsMap[mdItem.menuItemId]).filter(i => !!i),
        };

        return itemSection;
      })
      .filter(section => section.items.length > 0);

    return itemSections.concat(hiddenItemSections);
  }

  isItemDisplayedAsSelected(mode: BuilderMode, item: SelectableMenuItem) {
    return !!this.itemSelector
      .getAllSelectedItems()
      .find(i => i.item.itemId == item.item.itemId || !!(i.item.pricingReferenceItemId && i.item.pricingReferenceItemId == item.item.itemId));
  }

  getDisplayedItemPortion(mode: BuilderMode, item: SelectableMenuItem, currentVariant: SelectableVariantMenuItem | null): SelectableItemPortion | undefined {
    // Check if this item is the current variant:
    if (item == currentVariant) {
      // If the item isn't selected, bail out:
      if (!this.itemSelector.isItemSelected(item)) {
        return SelectableItemPortion.None;
      }

      const selectedPortions = this.itemSelector.getSelectedItems(item.portions || []);
      const halfPortions = getHalfPortions(selectedPortions);
      const extraPortions = getExtraPortions(selectedPortions);

      // If there are any half portions, return half:
      if (halfPortions.length) {
        return SelectableItemPortion.Half;
      }

      // If there's an extra portion for this variant, return extra:
      if (!!findItemReferencing(extraPortions, item.item.itemId)) {
        return SelectableItemPortion.Extra;
      }

      // If there's an extra portion for another variant, return single (since this variant is sharing the "extra"):
      if (!!this.itemSelector.getSelectedItems(extraPortions).length) {
        return SelectableItemPortion.Single;
      }

      // Finally, just default to single
      return SelectableItemPortion.Selected;
    }

    // Check if there's a corresponding portion in the current variant for this item...
    const portion = this.itemSelector.getSelectedItems(findItemsReferencing((currentVariant && currentVariant.portions) || [], item.item.itemId))[0];
    if (portion) {
      const type = portion.item.itemType as PortionItemType;

      return portionItemTypeToItemCardPortion(type, false);
    }

    // ...Otherwise just check the variant itself!
    return item && this.itemSelector.isItemSelected(item) ? SelectableItemPortion.Selected : SelectableItemPortion.None;
  }

  buildMealRequest(builderMode: BuilderMode, items: SelectableMenuItem[], mealName: string): OrderMealRequest | undefined {
    const variants = items.filter(i => i.itemSource == SelectableMenuItemSource.Variant) as SelectableVariantMenuItem[];
    const nonVariants = items.filter(i => i.itemSource == SelectableMenuItemSource.NonVariant);

    const meal: OrderMealRequest = {
      meal: {
        mealName,
        entrees: variants.map(variant => {
          return {
            menuItemId: variant.item.itemId,
            menuItemName: variant.item.itemName,
            quantity: variant.selectionInfo.kind == ItemCardSelectionInfoKind.Single ? 1 : this.itemSelector.getItemQuantity(variant),
            contents: variant.contents
              .filter(i => i.itemSource == SelectableMenuItemSource.VariantContents && this.itemSelector.isItemSelected(i))
              .map(i => this.selectableItemToOrderMealItem(i)),
          };
        }),
        sides: nonVariants.filter(i => i.item.itemCategory == MenuItemCategory.Side).map(i => this.selectableItemToOrderMealItem(i)),
        drinks: nonVariants.filter(i => i.item.itemCategory == MenuItemCategory.Drink).map(i => this.selectableItemToOrderMealItem(i)),
      },
    };

    return meal;
  }

  getEffectiveItemPrice(builderMode: BuilderMode, item: SelectableMenuItem, variant: SelectableVariantMenuItem): PricingInformation {
    const price = this.menu.getItemPrice(item.item);
    return {
      price,
      formattedPrice: price === 0 ? '' : this.utils.formatPrice(price, true),
    };
  }

  validateMealBuilder(mode: TBuilderMode, metadata: MenuMetadataForChannel, itemSections: MealBuilderItemSection[]): { error: string | null } {
    const validateItemSection = (section: MealBuilderItemSection) => {
      // Check for unsatisfied min quantity
      if (section.minQuantity && this.itemSelector.getSelectedItems(section.items).length < section.minQuantity) {
        return false;
      }

      // See if there are any item sections that need to be opted out of that haven't been opted out of or selected from
      if (section.showSelectNone && !(optedOutItemSectionIds.find(id => section.id == id) || any(section.items, i => this.itemSelector.isItemSelected(i)))) {
        return false;
      }

      return true;
    };

    const missingItemsSections = [];
    const optedOutItemSectionIds = this.store.state.mealBuilder.optedOutItemSectionIds;
    const variantsItemSectionTitle = this.getVariantsItemSectionTitle(mode);
    const variantsSectionMetadata = metadata.itemSections[variantsItemSectionTitle || ''];

    const partitionedItemSections = variantsSectionMetadata
      ? partition(itemSections, section => isDefined(section.sortOrder) && section.sortOrder < variantsSectionMetadata.sortOrder)
      : [[], itemSections];

    for (const section of partitionedItemSections[0]) {
      if (!validateItemSection(section)) {
        missingItemsSections.push(section.title);
      }
    }

    const variant = this.store.state.mealBuilder.selectedVariant;
    if (!variant) {
      missingItemsSections.push(variantsItemSectionTitle);
    }

    for (const section of partitionedItemSections[1]) {
      if (!validateItemSection(section)) {
        missingItemsSections.push(section.title);
      }
    }

    if (missingItemsSections.length) {
      this.store.commit(MUTATIONS.SET_MISSING_ITEM_SECTIONS, missingItemsSections);
      return {
        error: `Please choose your ${joinWithSuffix(
          missingItemsSections.map(s => s.toLowerCase()),
          ', ',
          missingItemsSections.length == 2 ? ' and ' : ', and ',
        )}`
      };
    }

    return { error: null };
  }

  setBuilderMode(mode: BuilderMode | null) {
    this.store.commit(MUTATIONS.SET_BUILDER_MODE, mode);
  }

  selectDefaultContents(mode: TBuilderMode, variant: SelectableVariantMenuItem) {
    const itemSections = this.getItemSections(mode, variant);
    const optedOutItemIds = this.store.state.mealBuilder.optedOutDefaultContentItemIds;

    for (const section of itemSections) {
      for (const item of section.items) {
        // We should auto-select if the item is a default content of the variant or it's in a section where there is only one option
        const shouldSelect = (item.item.defaultContent || (section.items.length == 1 && section.maxQuantity == 1)) && item.item.isItemAvailable;

        if (shouldSelect && !optedOutItemIds.find(id => id == item.item.itemId)) {
          this.itemSelector.selectItem(item);
        }
      }
    }
  }

  abstract selectVariantSafe(
    builderMode: TBuilderMode,
    chosenVariant: SelectableVariantMenuItem,
    currentVariant: SelectableVariantMenuItem | null,
    allVariants: SelectableVariantMenuItem[],
    args: any,
    shouldTrackAnalytics?: boolean,
  ): void;

  abstract toggleVariantCustomizationSafe(
    builderMode: TBuilderMode,
    variant: SelectableMenuItem,
    allVariants: SelectableVariantMenuItem[],
    customization: ItemCardCustomization,
    shouldTrackAnalytics?: boolean,
  ): void;

  abstract getVariants(builderMode: TBuilderMode): SelectableVariantMenuItem[];
  abstract getVariantsItemSectionTitle(builderMode: TBuilderMode): string;
  abstract getVariantsItemSectionSubtitle(mode: BuilderMode, variant: SelectableVariantMenuItem): string | undefined;
  abstract getDefaultVariant(builderMode: TBuilderMode): SelectableVariantMenuItem | undefined;

  protected getAdditionalItemSections(builderMode: TBuilderMode): MealBuilderItemSection[] {
    return [];
  }

  protected setSelectedVariant(variant: SelectableMenuItem | null) {
    this.store.commit(MUTATIONS.SET_SELECTED_VARIANT, variant);
  }

  protected isItemTypeHidden(itemType: string): boolean {
    return HIDDEN_ITEM_TYPES.has(itemType);
  }

  protected selectableItemToOrderMealItem(item: SelectableMenuItem): BasicOrderMealItem {
    const customization = item.customizations.find(c => c.selected && c.type == ItemCardCustomizationType.Customization) as MenuItemCardCustomization | undefined;

    return {
      menuItemId: item.item.itemId,
      menuItemName: item.item.itemName,
      quantity: this.itemSelector.getItemQuantity(item),
      isUpSell: false,
      customizationId: customization && customization.customization.id,
    };
  }
}
